import { useRef, useLayoutEffect } from "react";
import useSendAnalyticsEvent from "./useAnalytics";
import { analyticsEvents, analyticsTypes } from "lib/utils/constants";
import { useSearchParams } from "react-router-dom";
import { useToken } from "lib/contexts/TokenProvider";

interface UseLoadingPerformanceOptions {
  /**
   * The condition that determines when loading is complete
   */
  isLoadingComplete: boolean;
  /**
   * Whether to trigger event when item loaded in foreground
   */
  checkElementInForeground?: boolean;
  /**
   * Analytics event name (optional, defaults to FLO_CART_LOADING_TIME_REACT)
   */
  eventName?: string;
  /**
   * Additional metadata to send with analytics (optional)
   */
  additionalMetadata?: Record<string, any>;
}

/**
 * Custom hook to measure loading performance from component mount to completion
 */
export const useLoadingPerformance = ({
  isLoadingComplete,
  eventName = analyticsEvents.FLO_CART_LOADING_TIME_REACT,
  additionalMetadata = {},
}: UseLoadingPerformanceOptions) => {
  const loadingStartTimeRef = useRef<number | null>(null);
  const hasLoggedLoadingTime = useRef<boolean>(false);
  const analytics = useSendAnalyticsEvent();
  const {
    state: { productType },
  } = useToken();

  const shouldSendAnalytics = productType !== "BACKGROUND_CART";

  // Track when loading starts (component mount)
  useLayoutEffect(() => {
    if (!loadingStartTimeRef.current && shouldSendAnalytics) {
      loadingStartTimeRef.current = performance.now();
    }
  }, []);

  // Track when loading is complete
  useLayoutEffect(() => {
    if (
      isLoadingComplete &&
      loadingStartTimeRef.current &&
      !hasLoggedLoadingTime.current &&
      shouldSendAnalytics
    ) {
      const loadingEndTime = performance.now();
      const loadingDuration = loadingEndTime - loadingStartTimeRef.current;

      // Determine if we should send analytics based on foreground/background loading
      if (shouldSendAnalytics) {
        hasLoggedLoadingTime.current = true;
        analytics.sendAnalyticsEvent({
          eventName,
          eventFor: [analyticsTypes.SF_ANALYTICS],
          eventType: "load",
          metaData: {
            loadDuration: Math.round(loadingDuration),
            ...additionalMetadata,
          },
        });
      }
    }
  }, [isLoadingComplete, eventName, additionalMetadata, analytics, productType]);

  return {
    loadingDuration: loadingStartTimeRef.current ? performance.now() - loadingStartTimeRef.current : null,
  };
};
